<script setup lang="ts">
import IllustrativePenIcon from '@/assets/icons/IllustrativePenIcon.vue';
import type { IRequiredActionPrompt } from '@/composables/requiredAction/requiredAction.types';
import RequiredActionTarget from '@/core/elements/RequiredActionTarget.vue';
import { computed } from 'vue';

const emit = defineEmits<{
  'click:surface': [];
  'interact:required-action': [];
}>();

const props = withDefaults(
  defineProps<{
    fillAvailableHeight?: boolean;
    /** Takes the panel's spare height without ever giving up its own. */
    growAvailableHeight?: boolean;
    requiredActionPrompt?: IRequiredActionPrompt | null;
  }>(),
  {
    fillAvailableHeight: false,
    growAvailableHeight: false,
    requiredActionPrompt: null,
  },
);

const stretchInput = computed(() => props.fillAvailableHeight || props.growAvailableHeight);

const handleSurfaceClick = (event: MouseEvent): void => {
  if (event.target === event.currentTarget) emit('click:surface');
};
</script>

<template>
  <div
    class="mt-md flex min-h-header items-start gap-md pr-md"
    :class="[props.fillAvailableHeight && 'flex-1', props.growAvailableHeight && 'grow']"
  >
    <RequiredActionTarget :prompt="props.requiredActionPrompt" @dismiss="emit('interact:required-action')">
      <template #default="{ targetClass, describedBy }">
        <span
          :class="[
            'flex size-icon-button shrink-0 items-center justify-center rounded-button bg-highlight',
            targetClass,
          ]"
          :aria-describedby="describedBy"
        >
          <IllustrativePenIcon class="size-icon-sm" />
        </span>
      </template>
    </RequiredActionTarget>
    <div
      class="min-w-0 flex-1"
      :class="[stretchInput && 'self-stretch', props.growAvailableHeight && 'cursor-text']"
      @click="handleSurfaceClick"
    >
      <!-- Anchor suggestions to the text height, independently of the spare clickable space. -->
      <div class="relative" :class="props.fillAvailableHeight && 'h-full'">
        <slot></slot>
      </div>
    </div>
  </div>
</template>
